/**
 * components/ui/glass.tsx
 * ------------------------------------------------------------------
 * Acepsle's visual language — warm, friendly, modern (FreshBooks /
 * Wave DNA) with Acepsle orange and one signature twist: BUTTONS ARE
 * SHARP, everything else is softly ROUNDED.
 *
 *   - Surfaces (cards, modals, inputs, chips, badges, nav, toggles)
 *     are rounded with soft shadows on solid warm fills.
 *   - Buttons / primary calls-to-action are sharp (rounded-none) —
 *     a crisp orange block against the soft surfaces.
 *
 * (The file keeps its `glass` name for import stability; the look is
 * now solid + soft-shadowed rather than frosted.)
 * ------------------------------------------------------------------
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/* ---- Backdrop ----------------------------------------------------
 * Calm warm ground with one barely-there warm wash. No gradients.
 * ---------------------------------------------------------------- */
/* ---- Backdrop ----------------------------------------------------
 * The AcePSLE "warm glow" background, minus the floating motion. A
 * soft warm gradient (orange-50 -> amber-50 -> white in light, near-
 * black grays in dark) plus a barely-there grid for texture. No
 * animated blobs, sparkles, or drifting particles. White cards sit
 * on top and pop. Sits behind everything (fixed, -z-10).
 * ---------------------------------------------------------------- */
export function Backdrop() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* warm-glow base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50/80 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950" />
      {/* subtle grid texture (3% light / 6% dark) */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.6) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
    </div>
  );
}

/* ---- Hero --------------------------------------------------------
 * Rounded solid header card with a rounded orange icon tile.
 * ---------------------------------------------------------------- */
export function Hero({
  icon: Icon,
  title,
  subtitle,
  actions,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="surface rounded-card p-6 sm:p-7">
      <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand text-white shadow-card">
            <Icon className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-primary dark:text-white">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-sm text-primaryLight dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
}

/** Back-compat alias for the migrated comms hub. */
export const GradientHero = Hero;

/* ---- SoftCard ----------------------------------------------------
 * Rounded solid card with a soft shadow. Optional orange left accent.
 * ---------------------------------------------------------------- */
export function SoftCard({
  children,
  className = '',
  accent = false,
  hover = false,
  pad = 'p-5',
}: {
  children: ReactNode;
  className?: string;
  accent?: boolean;
  hover?: boolean;
  pad?: string;
}) {
  return (
    <div
      className={`surface ${
        accent ? 'rounded-l-none rounded-r-card border-l-4 border-l-brand' : 'rounded-card'
      } ${pad} ${hover ? 'transition-shadow hover:shadow-cardHover' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

/* ---- StatChip ----------------------------------------------------
 * Rounded solid pale-tint stat tile (no gradient).
 * ---------------------------------------------------------------- */
const CHIP: Record<string, { bg: string; val: string }> = {
  slate: {
    bg: 'bg-gray-100 dark:bg-white/5',
    val: 'text-gray-700 dark:text-gray-200',
  },
  brand: {
    bg: 'bg-brandSoft dark:bg-brand/10',
    val: 'text-brand',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-500/10',
    val: 'text-green-700 dark:text-green-300',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-500/10',
    val: 'text-amber-700 dark:text-amber-300',
  },
  red: {
    bg: 'bg-red-50 dark:bg-red-500/10',
    val: 'text-red-700 dark:text-red-300',
  },
};

export function StatChip({
  tone = 'slate',
  value,
  label,
  icon: Icon,
}: {
  tone?: keyof typeof CHIP;
  value: ReactNode;
  label: string;
  icon?: LucideIcon;
}) {
  const c = CHIP[tone] ?? CHIP.slate!;
  return (
    <div className={`rounded-2xl p-4 text-center ${c.bg}`}>
      <div
        className={`flex items-center justify-center gap-1 text-2xl font-bold ${c.val}`}
      >
        {Icon && <Icon size={18} />}
        {value}
      </div>
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        {label}
      </div>
    </div>
  );
}

/* ---- AccentButton ------------------------------------------------
 * Solid-orange primary action. SHARP corners (our signature).
 * ---------------------------------------------------------------- */
export function AccentButton({
  icon: Icon,
  children,
  onClick,
  disabled,
  type = 'button',
  title,
}: {
  icon?: LucideIcon;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex items-center gap-2 bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-card transition hover:bg-brandHover disabled:cursor-not-allowed disabled:opacity-50"
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

/* ---- glassFieldClass --------------------------------------------
 * Chunky ROUNDED input/select/textarea class.
 * ---------------------------------------------------------------- */
export function glassFieldClass(extra = ''): string {
  return `w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5 text-sm text-ink transition-all placeholder:text-gray-400 hover:border-gray-300 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/15 dark:border-white/10 dark:bg-white/5 dark:text-gray-100 dark:hover:border-white/20 ${extra}`;
}

/* ---- glassSurface -----------------------------------------------
 * Frosted translucent surface for form overlays/modals — true
 * glassmorphism (backdrop-blur, semi-transparent, faint ring).
 * ---------------------------------------------------------------- */
export function glassSurface(extra = ''): string {
  return `rounded-card border border-white/60 bg-white/85 shadow-pop ring-1 ring-black/5 backdrop-blur-xl dark:border-white/10 dark:bg-gray-900/80 ${extra}`;
}

/* ---- GlassModal --------------------------------------------------
 * Rounded solid modal with a dim backdrop blur.
 * ---------------------------------------------------------------- */
export const glassModalBackdrop =
  'fixed inset-0 z-50 flex items-center justify-center p-4 bg-primary/40 backdrop-blur-sm';

export function GlassModal({
  title,
  onClose,
  children,
  footer,
  maxWidth = 'max-w-md',
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
}) {
  return (
    <div className={glassModalBackdrop} onClick={onClose}>
      <div
        className={glassSurface(`w-full ${maxWidth} max-h-[90vh] overflow-y-auto`)}
        onClick={(e) => e.stopPropagation()}
      >
        {title !== undefined && (
          <div className="flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <h2 className="text-base font-semibold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        )}
        <div className="p-5">{children}</div>
        {footer !== undefined && (
          <div className="flex items-center justify-end gap-2 border-t border-black/5 px-5 py-4 dark:border-white/10">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
